import { FormattedDate, FormattedMessage } from 'react-intl'
import { View } from 'react-native'
import styled from 'styled-components'
import { parseJSON } from 'date-fns'
import PropTypes from 'prop-types'
import Image from '@flix/common/elements/images/Image/Image'
import { Html, Text } from '@flix/common/elements/typography'
import { InvertedThemeProvider } from '@flix/common/style/InvertedThemeProvider'
import { media, useMediaIs } from '@flix/common/style/media'
import { ellipsis } from '@flix/common/utils/strings'
import { bold } from '../../../../style/commonStyles'
import { AdCTAButton } from '../../../ads/AdCTAButton/AdCTAButton'
import messages from '../../messages'

// A good average height for the image across different widths to ensure equal
// height for different block widths / padding.
const IMG_HEIGHT = 220

const Category = styled(Text)`
  color: #fff;
  background-color: rgba(0, 0, 0, 0.55);
  font-size: ${(props) => props.theme.fontSizes.xsmall}px;
  padding: ${(props) => props.theme.spacing.xs}px ${(props) => props.theme.spacing.sm}px;
  position: absolute;
  top: ${(props) => props.theme.spacing.medium}px;
  right: 0;
`

const ImgPlaceholder = styled(View)`
  width: 100%;
  height: ${IMG_HEIGHT}px;
  background-color: ${(props) => props.theme.colors.background};
`

const Body = styled(View)`
  justify-content: space-between;
  padding: ${(props) => props.theme.spacing.sm}px ${(props) => props.theme.spacing.smedium}px;
  ${media.sm`
    height: 220px;
  `};
  ${media.md`
    height: 180px;
  `};
  ${media.lg`
    height: 220px;
  `};
`

// Title can contain i/b tags, wrapping in `<h3>` required by RN Html renderer
const TitleHtml = styled(Html)`
  margin-bottom: ${(props) => props.theme.spacing.md}px;
`

const Description = styled(Html)`
  margin-bottom: ${(props) => props.theme.spacing.sm}px;
`

const BottomRow = styled(View)`
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: ${(props) => props.theme.spacing.xs}px;
  /* align-self: flex-end; */
`

const PublishDate = styled(Text)`
  font-size: ${(props) => props.theme.fontSizes.small}px;
  ${bold};
`

const ReadMore = styled(Text)`
  ${bold};
  font-size: ${(props) => props.theme.fontSizes.small}px;
  text-decoration-line: underline;
  color: ${(props) => props.theme.colors.primaryDark};
`

export const ArticleBlockContent = ({ width, article, pinnedImgIsWide, showCategory = true }) => {
  const sm = useMediaIs('sm')

  const imgSrc = article.externalImage && { uri: article.externalImage }
  const topBannerImg = article.topBannerImg
  const listPreviewImg = article.listPreviewImg
  const banner = pinnedImgIsWide && article.pinned ? topBannerImg : listPreviewImg || topBannerImg
  return (
    <>
      {Boolean(width) ? (
        <Image width={Math.ceil(width)} height={IMG_HEIGHT} publicId={banner} source={imgSrc} />
      ) : (
        <ImgPlaceholder />
      )}
      {showCategory && <Category>{article.categories[0].label}</Category>}
      <InvertedThemeProvider>
        <Body>
          <View>
            {article.title && (
              <TitleHtml fontSize="large" content={`<b>${ellipsis(95, article.title)}</b>`} />
            )}
            {article.listDescription && (
              <Description
                fontSize="small"
                // Phones don't have fixed height blocks, so it's fine to allow more characters
                content={ellipsis(sm ? 220 : 320, article.listDescription)}
              />
            )}
          </View>
          <BottomRow>
            <PublishDate>
              {article.date && (
                <FormattedDate
                  value={parseJSON(article.date)}
                  day="numeric"
                  month="long"
                  year="numeric"
                />
              )}
            </PublishDate>
            {article.cta ? (
              <AdCTAButton {...article.cta} />
            ) : (
              <ReadMore>
                <FormattedMessage {...messages.readMore} />
              </ReadMore>
            )}
          </BottomRow>
        </Body>
      </InvertedThemeProvider>
    </>
  )
}

ArticleBlockContent.propTypes = {
  width: PropTypes.number.isRequired,
  article: PropTypes.object.isRequired,
  pinnedImgIsWide: PropTypes.bool,
  showCategory: PropTypes.bool,
}
